import {
  Avatar,
  Box,
  Checkbox,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AppLoader from "../../../../components/AppLoader/index.js";
import AuthContainer from "../../../../components/AuthContainer.js";
import BackButton from "../../../../components/BackButton/index.js";
import GlobalButton from "../../../../components/CustomButton/index.js";
import { getAllServicesWithSubServices } from "../../../../services/company/serviceCategoryApiFn.js";
import { handleOpenSnackBar } from "../../../../utils";
import { useSelector } from "react-redux";

const MAIN_SERVICES = 1;
const SUB_SERVICES = 2;

const WorkerServices = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const { currentLanguage } = useSelector((state) => state.translationReducer);

  useEffect(() => {
    fetchAllServiceWithSubServices();
  }, []);

  const fetchAllServiceWithSubServices = async () => {
    try {
      setIsLoading(true);
      const response = await getAllServicesWithSubServices();

      if (response?.data?.status === "success") {
        const allServicesData = response?.data?.data;

        const selectedServices = state?.selectedServices || [];

        const tempArray = allServicesData.map((service) => {
          const selectedService = selectedServices.find(
            (selected) => selected.services === service._id
          );

          return {
            ...service,
            isSelected: !!selectedService,
            subservices: service.subservices.map((sub) => ({
              ...sub,

              isSelected: selectedService
                ? selectedService.subServices.includes(sub._id)
                : false,
            })),
          };
        });

        setAllServices(tempArray);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectMainService = (index) => {
    const updatedServices = [...allServices];
    const mainService = updatedServices[index];
    mainService.isSelected = !mainService.isSelected;

    if (!mainService.isSelected) {
      mainService.subservices = mainService.subservices.map((sub) => ({
        ...sub,
        isSelected: false,
      }));
    }
    setAllServices(updatedServices);
  };

  const handleSelectSubService = (mainIndex, subIndex) => {
    const updatedServices = [...allServices];
    updatedServices[mainIndex].subservices[subIndex].isSelected =
      !updatedServices[mainIndex].subservices[subIndex].isSelected;
    setAllServices(updatedServices);
  };

  const handleGotoNext = () => {
    // Filter selected main services and sub-services
    const selectedServices = allServices
      .filter((service) => service.isSelected)
      .map((service) => ({
        services: service._id,
        subServices: service.subservices
          .filter((sub) => sub.isSelected)
          .map((sub) => sub._id),
      }));

    if (selectedServices.length === 0) {
      handleOpenSnackBar("error", t("selectAtLeastOneMainService"));
      return;
    }

    if (selectedServices.every((service) => service.subServices.length === 0)) {
      handleOpenSnackBar("error", t("selectAtLeastOneSubService"));
      return;
    }

    if (selectedServices.length > MAIN_SERVICES) {
      handleOpenSnackBar(
        "error",
        t("maxOneMainService", {
          mainServiceCount: MAIN_SERVICES,
        })
      );
      return;
    }

    for (const service of selectedServices) {
      if (service.subServices.length > SUB_SERVICES) {
        handleOpenSnackBar(
          "error",
          t("maxOneMainService", {
            subServiceCount: SUB_SERVICES,
          })
        );
        return;
      }
    }

    navigate("/register/worker", { state: { selectedServices } });
  };

  return (
    <AuthContainer>
      <AppLoader isVisible={isLoading} />
      <BackButton
        handleBack={() => {
          navigate("/login", { state: { type: "worker" } });
        }}
      />

      <Container sx={{ maxWidth: "520px !important", mt: 4 }}>
        <Stack textAlign={"center"}>
          <Typography
            variant="auth_primary_text"
            component={"h1"}
            textTransform={"capitalize"}
          >
            {t("selectServices")}
          </Typography>
          <Typography variant="auth_secondary_text" component={"p"}>
            {t("selectServiceInstruction")}
          </Typography>
        </Stack>
      </Container>

      <Paper sx={{ p: 2 }}>
        <Stack spacing={2}>
          {allServices?.map((item, index) => (
            <Stack
              key={index}
              direction="column"
              spacing={1}
              bgcolor="#f1f1f1"
              p={1}
              border="1px solid #e9e8e8"
            >
              <Stack direction="row" spacing={1}>
                <Checkbox
                  checked={item.isSelected}
                  onChange={() => handleSelectMainService(index)}
                />
                <Stack direction="row" spacing={1}>
                  <Avatar
                    src={item.image}
                    sx={{
                      height: 70,
                      width: 70,
                      borderRadius: "6px",
                    }}
                  />
                  <Box>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      textTransform="capitalize"
                    >
                      {item.name[currentLanguage]}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {item.description[currentLanguage]}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              {/* Subservices */}
              {item.isSelected &&
                item.subservices.map((sub, subIndex) => (
                  <Stack
                    key={subIndex}
                    direction="row"
                    spacing={1}
                    pl={4}
                    alignItems="center"
                    bgcolor={"#ffffff"}
                    py={1}
                  >
                    <Checkbox
                      checked={sub.isSelected}
                      onChange={() => handleSelectSubService(index, subIndex)}
                    />
                    <Avatar
                      src={sub.activeIcon}
                      sx={{
                        height: 50,
                        width: 50,
                        borderRadius: "6px",
                      }}
                    />
                    <Box>
                      <Typography
                        variant="body1"
                        fontWeight={600}
                        textTransform={"capitalize"}
                      >
                        {sub.name[currentLanguage]}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {sub.description[currentLanguage]}
                      </Typography>
                    </Box>
                  </Stack>
                ))}
            </Stack>
          ))}
        </Stack>

        <Stack my={3} alignItems={"flex-end"}>
          <GlobalButton
            btnText={"update"}
            handleClickBtn={handleGotoNext}
            styleProps={{
              width: 150,
              height: 40,
            }}
          />
        </Stack>
      </Paper>
    </AuthContainer>
  );
};

export default WorkerServices;
